import { LogtoProvider } from "@logto/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { defaultCampaignFilterKeys } from "./components/filter/components/filter/filter-configuration";
import { FilterContextProvider } from "./components/filter/FilterContext";
import { SearchContextProvider } from "./components/filter/SearchContext";
import { SortContextProvider } from "./components/filter/SortContext";
import { Toaster } from "./components/ui/Toaster";
import { TooltipProvider } from "./components/ui/Tooltip";
import { ConfirmationProvider } from "./contexts/ConfirmationProvider";
import { ContactBoardContextProvider } from "./contexts/ContactBoardContext";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { LogtoApolloProvider } from "./contexts/LogtoApolloContext";
import { UserProvider } from "./contexts/UserContext";
import { authConfig } from "./lib/auth-config";
import { SerializedRefreshLogtoClient } from "./lib/serialized-refresh-logto-client";
import moment from "moment";
import { HelmetProvider } from "react-helmet-async";
import { Router } from "./router";
// @ts-expect-error package does not have types
import deLocale from "moment/locale/de";
import type { ComponentProps, ReactNode } from "react";

type LogtoClientClass = NonNullable<
  ComponentProps<typeof LogtoProvider>["LogtoClientClass"]
>;

moment.updateLocale("de", deLocale);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: (query) => query.state.status !== "error",
      retry: 2,
    },
  },
});

export function App() {
  return (
    <AppProviders>
      <Router />
      <Toaster />
    </AppProviders>
  );
}

function AppProviders({ children }: { readonly children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <LogtoProvider
        config={authConfig}
        LogtoClientClass={
          SerializedRefreshLogtoClient as unknown as LogtoClientClass
        }
      >
        <ImpersonationProvider>
          <UserProvider>
            <LogtoApolloProvider>
              <ConfirmationProvider>
                <TooltipProvider delayDuration={0}>
                  <HelmetProvider>
                    <AppFeatureProviders>{children}</AppFeatureProviders>
                  </HelmetProvider>
                </TooltipProvider>
              </ConfirmationProvider>
            </LogtoApolloProvider>
          </UserProvider>
        </ImpersonationProvider>
      </LogtoProvider>
    </QueryClientProvider>
  );

  function AppFeatureProviders({ children }: { readonly children: ReactNode }) {
    return (
      <FilterContextProvider defaultFilters={defaultCampaignFilterKeys}>
        <SortContextProvider>
          <SearchContextProvider>
            <ContactBoardContextProvider>{children}</ContactBoardContextProvider>
          </SearchContextProvider>
        </SortContextProvider>
      </FilterContextProvider>
    );
  }
}
